import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { AccordionDetails, AccordionSummary } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import Image from 'next/image';

import { nutritionUnits } from '@utils/nutritionUnits';
import theme from 'src/style-system/theme';

import ImageCarousel from '@components/ImageCarousel';
import Accordion from '@components/Accordian';
import CuminButton from '@components/Button';
import {
  productType,
  nutritionType,
  getCategorizedProductsType,
} from '@utils/types';
import { GetFilledChilli } from '@components/FilledChilli';
import { GetEmptyChilli } from '@components/EmptyChilli';
import { GetVegan } from '@components/Vegan';
import ArrowTooltips from '@components/Tooltip';
import ModalComponent from '@components/ModalComponent';

import { useStyles } from './useStyles';

interface IBundleModal {
  open: boolean;
  handleClose: () => void;
  category?: getCategorizedProductsType;
  handleAddToCart: (products: productType[], category_name: string) => void;
}

const BundleModal = ({
  open,
  handleClose,
  category,
  handleAddToCart,
}: IBundleModal) => {
  const {
    category_name,
    categoryImage,
    products,
    category_id,
    secondaryImages,
    categoryDescription,
  } = category || {};

  const classes = useStyles();

  const onAddToCart = (products?: productType[], category_name?: string) => {
    if (products) handleAddToCart(products, category_name || '');
    handleClose();
  };

  const getSpices = (spice_level: number) => {
    return (
      <>
        {spice_level >= 1 ? <GetFilledChilli /> : <GetEmptyChilli />}
        {spice_level >= 2 ? <GetFilledChilli /> : <GetEmptyChilli />}
        {spice_level >= 3 ? <GetFilledChilli /> : <GetEmptyChilli />}
        {spice_level >= 4 ? <GetFilledChilli /> : <GetEmptyChilli />}
        {spice_level >= 5 ? <GetFilledChilli /> : <GetEmptyChilli />}
      </>
    );
  };

  return (
    <ModalComponent open={open} handleClose={handleClose}>
      <Box className={classes.mainContainer}>
        <CloseIcon className={classes.closeIcon} onClick={handleClose} />

        {secondaryImages && Array.isArray(secondaryImages) ? (
          <Box className={classes.carouselContainer}>
            <ImageCarousel carouselContent={secondaryImages} />
          </Box>
        ) : categoryImage ? (
          <Box className={classes.categoryImgWrapper}>
            <Image
              src={categoryImage}
              alt={category_name}
              objectFit="cover"
              layout="fill"
            />
          </Box>
        ) : null}

        <Box className={classes.contentWrapper}>
          <Typography variant="h2" align="center" color="cinnamon.main" mb={2}>
            {category_name}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            color="cumin.main"
            mb="20px"
          >
            {categoryDescription}
          </Typography>

          <Box className={classes.addToCartBtnWrapper}>
            <CuminButton
              color="cinnamon"
              variant="contained"
              textColor="whitePepper.main"
              width="200px"
              height="48"
              borderRadius="24"
              onClick={() => onAddToCart(products, category_name)}
            >
              Add to Cart
            </CuminButton>
          </Box>
          <Typography
            component="div"
            variant="caption"
            align="center"
            color="cumin.main"
            m="20px 0"
          >
            This bundle includes
          </Typography>

          {products?.map((product) => {
            const {
              id,
              title,
              image_url,
              description,
              taste,
              spice_level,
              vegan,
              ingredients,
              nutrition,
              how_to_prepare,
            } = product || {};
            return (
              <Box key={id}>
                <Accordion
                  bgcolor={theme.palette.whitePepper.main}
                  bordercolor={theme.palette.cumin.main}
                  marginbottom={15}
                >
                  <AccordionSummary
                    expandIcon={
                      <ArrowDropDownIcon
                        fontSize="large"
                        sx={{ color: 'garamMasala.main' }}
                      />
                    }
                    aria-controls="panel1a-content"
                  >
                    <Typography
                      variant="body1"
                      align="left"
                      color="cinnamon.main"
                    >
                      {title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box className={classes.imgSpiceLevelSection}>
                      <Box className={classes.dishImgWrapper}>
                        {image_url ? (
                          <Image
                            src={image_url}
                            objectFit="cover"
                            layout="fill"
                          />
                        ) : null}
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          align="left"
                          color="cumin.main"
                        >
                          {taste}
                        </Typography>
                        <Box className={classes.symbolWrapper}>
                          {vegan ? (
                            <ArrowTooltips title="Vegan">
                              <GetVegan />
                            </ArrowTooltips>
                          ) : null}
                          {product?.spice_level !== 0 ? (
                            <ArrowTooltips title="Spice Level">
                              {getSpices(spice_level)}
                            </ArrowTooltips>
                          ) : null}
                        </Box>
                      </Box>
                    </Box>
                    <Typography
                      variant="body2"
                      align="left"
                      color="garamMasala.main"
                      m="15px 0 30px"
                    >
                      {description}
                    </Typography>
                    <Typography
                      variant="body2"
                      align="left"
                      color="cinnamon.main"
                    >
                      Ingredients
                    </Typography>
                    <Typography
                      variant="body2"
                      align="justify"
                      color="cumin.main"
                      m="10px 0 20px"
                    >
                      {ingredients}
                    </Typography>
                    <Typography
                      variant="body2"
                      align="left"
                      color="cinnamon.main"
                    >
                      Nutrition Facts
                    </Typography>
                    <Typography
                      variant="body2"
                      align="justify"
                      color="cumin.main"
                      m="10px 0 20px"
                    >
                      {nutrition
                        ?.filter((item: nutritionType) => item?.value > 0)
                        ?.map(
                          (item: nutritionType) =>
                            ` ${item?.name} ${item?.value} ${
                              nutritionUnits[item?.name]
                            }`
                        )
                        ?.toString()}
                    </Typography>
                    <Typography
                      variant="body2"
                      align="left"
                      color="cinnamon.main"
                    >
                      How to Prepare
                    </Typography>
                    <Typography
                      variant="body2"
                      align="justify"
                      color="cumin.main"
                      m="10px 0 20px"
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: how_to_prepare,
                        }}
                      ></div>
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Box>
            );
          })}
          <Box className={classes.addToCartBtnWrapper} mt="40px">
            <CuminButton
              color="cinnamon"
              variant="contained"
              textColor="whitePepper.main"
              width="200px"
              height="48"
              borderRadius="24"
              onClick={() => onAddToCart(products, category_name)}
            >
              Add to Cart
            </CuminButton>
          </Box>
        </Box>
      </Box>
    </ModalComponent>
  );
};

export default BundleModal;
